import React, { useEffect, useState } from 'react';

import { type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Translate, TranslateFunction } from 'react-localize-redux';

import { Button, EnableSwitch, ExternalLink, InputField, Typography } from '@fd/ui/atoms';
import { FormSection } from '@fd/ui/molecules';

import { WebOrderPage, webOrderPageService } from './webOrderPage.service';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginBottom: theme.spacing(5),
  },
  textContainer: {
    marginBottom: theme.spacing(3),
  },
  pathContainer: {
    marginTop: theme.spacing(3),
  },
}));

const MAX_PATH_LENGTH = 2048;

// Mirrors the API rules: empty, or starts with a single "/", no query, fragment, backslash or whitespace.
export const isValidOrderPagePath = (path: string) =>
  path === '' ||
  (path.length <= MAX_PATH_LENGTH && path.startsWith('/') && !/^\/\/|[?#\s\\]/.test(path));

const queryKey = (appId: string) => ['webOrderPage', appId];

type Props = {
  appId: string;
  baseUrl: string;
  isDisabled?: boolean;
  onError: (error?: unknown) => void;
  onSaved: () => void;
  translate: TranslateFunction;
};

export const WebOrderPageSettings = ({
  appId,
  baseUrl,
  isDisabled,
  onError,
  onSaved,
  translate,
}: Props) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKey(appId),
    queryFn: () => webOrderPageService.getWebOrderPage(appId),
  });
  const [path, setPath] = useState('');

  useEffect(() => {
    if (data) {
      setPath(data.OrderPagePath);
    }
  }, [data]);

  const { mutate, isPending } = useMutation({
    mutationFn: (update: WebOrderPage) => webOrderPageService.updateWebOrderPage(appId, update),
    onSuccess: (_result, update) => {
      queryClient.setQueryData(queryKey(appId), update);
      onSaved();
    },
    onError,
  });

  const savedPath = data?.OrderPagePath ?? '';
  const isManual = data?.Mode === 'Manual';
  const isPathValid = isValidOrderPagePath(path);

  // Switching to Automatic blanks the path until the next qualifying live order.
  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!data) {
      return;
    }
    mutate(
      event.target.checked
        ? { Mode: 'Automatic', OrderPagePath: '' }
        : { Mode: 'Manual', OrderPagePath: data.OrderPagePath }
    );
  };

  return (
    <FormSection
      sectionTitle={translate('Link_to_your_ordering_page') as string}
      showDivider={false}
      isLoading={isLoading}
    >
      <div className={classes.container}>
        <div className={classes.textContainer}>
          <Typography variantMapping={{ caption: 'p' }} variant="caption">
            <Translate id="Link_to_your_ordering_page_description" />
          </Typography>
        </div>
        {isError ? (
          <>
            <Typography variant="body1">
              <Translate id="Error_please_try_again_later" />
            </Typography>
            <div className={classes.pathContainer}>
              <Button fdKey="web-order-page-retry" variant="secondary" onClick={() => refetch()}>
                <Translate id="Retry" />
              </Button>
            </div>
          </>
        ) : (
          <Typography variant="body1">
            {isValidOrderPagePath(savedPath) ? (
              <ExternalLink
                href={`${baseUrl}${savedPath}`}
              >{`${baseUrl}${savedPath}`}</ExternalLink>
            ) : (
              `${baseUrl}${savedPath}`
            )}
          </Typography>
        )}
        <div className={classes.pathContainer}>
          <EnableSwitch
            fdKey="web-order-page-automatic-toggle"
            checked={data?.Mode === 'Automatic'}
            onChange={handleModeChange}
            disabled={isDisabled || isPending || !data}
            inputProps={{
              'aria-label': translate('Flipdish_controls_this_automatically') as string,
            }}
          />
          <Translate id="Flipdish_controls_this_automatically" />
          <Typography variantMapping={{ caption: 'p' }} variant="caption">
            <Translate
              id={isManual ? 'Ordering_page_manual_help' : 'Ordering_page_automatic_help'}
            />
          </Typography>
        </div>
        {isManual && (
          <div className={classes.pathContainer}>
            <InputField
              fdKey="web-order-page-path-input"
              fullWidth
              variant="outlined"
              label={translate('Ordering_page_path') as string}
              placeholder="/order"
              value={path}
              onChange={(event) => setPath(event.target.value)}
              disabled={isDisabled || isPending}
              error={!isPathValid}
              helperText={!isPathValid ? translate('Path_to_web_order_invalid') : undefined}
            />
            <div className={classes.pathContainer}>
              <Button
                fdKey="web-order-page-save"
                variant="primary"
                disabled={isDisabled || isPending || !isPathValid || path === data?.OrderPagePath}
                onClick={() => mutate({ Mode: 'Manual', OrderPagePath: path })}
              >
                <Translate id="Save" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </FormSection>
  );
};
